'use client';

import { useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import { toast } from 'sonner';
import {
  CONVENTION_TAG_CATEGORY_ORDER,
  groupConventionTags,
  normalizeConventionTag,
  type ConventionTagCategory,
} from '../../../../lib/conventionTags';

type ConventionTagEditorProps = {
  value: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
  maxTags?: number;
  placeholder?: string;
};

const categoryLabelKeys: Record<ConventionTagCategory, string> = {
  language: 'tagCategoryLanguage',
  framework: 'tagCategoryFramework',
  platform: 'tagCategoryPlatform',
  domain: 'tagCategoryDomain',
};

export function ConventionTagEditor({
  value,
  onChange,
  disabled,
  maxTags = 30,
  placeholder,
}: ConventionTagEditorProps) {
  const t = useTranslations('conventions.tags');
  const [tagDraft, setTagDraft] = useState('');
  const definedGroups = useMemo(() => groupConventionTags(), []);

  const addTag = (raw: string) => {
    const normalized = normalizeConventionTag(raw);
    if (!normalized) {
      return;
    }
    if (value.some((existing) => existing.toLowerCase() === normalized.toLowerCase())) {
      setTagDraft('');
      return;
    }
    if (value.length >= maxTags) {
      toast.error(t('errorTooManyTags'));
      return;
    }
    onChange([...value, normalized]);
    setTagDraft('');
  };

  const removeTag = (tag: string) => {
    onChange(value.filter((existing) => existing !== tag));
  };

  const toggleDefinedTag = (tag: string) => {
    const exists = value.some((existing) => existing.toLowerCase() === tag.toLowerCase());
    if (exists) {
      removeTag(tag);
    } else {
      addTag(tag);
    }
  };

  return (
    <div>
      {value.length > 0 ? (
        <div className="mb-2 flex flex-wrap gap-1.5">
          {value.map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs"
              style={{
                background: 'var(--chip-info-bg)',
                color: 'var(--chip-info-fg)',
              }}
            >
              {tag}
              <button
                type="button"
                className="opacity-60 hover:opacity-100"
                onClick={() => removeTag(tag)}
                aria-label={t('tagRemoveAria', { tag })}
                disabled={disabled}
              >
                x
              </button>
            </span>
          ))}
        </div>
      ) : null}
      <input
        className="input input-bordered input-sm w-full"
        value={tagDraft}
        onChange={(event) => setTagDraft(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ',') {
            event.preventDefault();
            addTag(tagDraft);
          } else if (event.key === 'Backspace' && tagDraft.length === 0 && value.length > 0) {
            event.preventDefault();
            removeTag(value[value.length - 1]);
          }
        }}
        onBlur={() => {
          if (tagDraft.trim()) {
            addTag(tagDraft);
          }
        }}
        placeholder={placeholder ?? t('tagInputPlaceholder')}
        disabled={disabled}
      />

      <div className="mt-3 space-y-3">
        {CONVENTION_TAG_CATEGORY_ORDER.map((category) => {
          const items = definedGroups.get(category) ?? [];
          if (items.length === 0) {
            return null;
          }
          return (
            <div key={category}>
              <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-[var(--color-fg-subtle)]">
                {t(categoryLabelKeys[category])}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {items.map((tag) => {
                  const selected = value.some((existing) => existing.toLowerCase() === tag.toLowerCase());
                  return (
                    <button
                      type="button"
                      key={tag}
                      className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs transition"
                      style={
                        selected
                          ? {
                              background: 'var(--chip-primary-bg)',
                              color: 'var(--chip-primary-fg)',
                              borderColor: 'transparent',
                            }
                          : {
                              background: 'transparent',
                              borderColor: 'var(--color-border, rgba(0,0,0,0.15))',
                            }
                      }
                      onClick={() => toggleDefinedTag(tag)}
                      disabled={disabled}
                    >
                      {tag}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
